import { api } from "@glasshalf/api-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, RefreshCcw, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ConfirmImpactDialog } from "../../components/ConfirmImpactDialog";
import { EmptyState } from "../../components/EmptyState";
import { StatusBadge } from "../../components/StatusBadge";
import { compactLabel, formatDateTime, formatMoney, formatNumber, formatSizeLabel } from "../../lib/format";
import { productRows } from "../../lib/selectors";
import type { AnyRecord, AppData, ProductRow, SkuRow } from "../../lib/types";

type ProductDetailPageProps = {
  data: AppData;
};

type ColorwayEdit = {
  code: string;
  name: string;
};

type ProductForm = {
  productName: string;
  categoryCode: string;
  categoryCode4: string;
  price: number;
  summaryDescription: string;
  description: string;
  sizeGuide: string;
  materialCare: string;
  colorways: ColorwayEdit[];
};

type PendingAction = "register" | "match" | "delete" | null;

type ProductCategoryPreset = {
  group: string;
  name: string;
  code: string;
};

type ProductReviewItem = {
  key: string;
  title: string;
  body: string;
  status: string;
  tone: "default" | "success" | "warning" | "danger" | "info";
};

const COLOR_OPTION_NAMES = ["color", "colour", "colorway", "color name", "색상", "색상명", "컬러", "컬러명"];

const PRODUCT_CATEGORY_PRESETS: ProductCategoryPreset[] = [
  { group: "아우터", name: "재킷", code: "JCKT" },
  { group: "아우터", name: "코트", code: "COAT" },
  { group: "아우터", name: "파카", code: "PARK" },
  { group: "아우터", name: "베스트", code: "VEST" },
  { group: "아우터", name: "플리스", code: "FLCE" },
  { group: "아우터", name: "쉘/윈드브레이커", code: "SHLL" },
  { group: "티셔츠", name: "반팔 티셔츠", code: "TSHT" },
  { group: "티셔츠", name: "롱슬리브 티셔츠", code: "LSTH" },
  { group: "티셔츠", name: "탱크톱", code: "TNKT" },
  { group: "폴로", name: "폴로 셔츠", code: "POLO" },
  { group: "폴로", name: "롱슬리브 폴로 셔츠", code: "LSPO" },
  { group: "셔츠", name: "셔츠", code: "SHRT" },
  { group: "셔츠", name: "오버셔츠", code: "OSHT" },
  { group: "셔츠", name: "유틸리티 셔츠", code: "WSHR" },
  { group: "스웨트", name: "스웨트셔츠", code: "SWSH" },
  { group: "스웨트", name: "후디", code: "HOOD" },
  { group: "스웨트", name: "집업 후디", code: "ZHOD" },
  { group: "스웨트", name: "스웨트팬츠", code: "SWPT" },
  { group: "니트", name: "니트웨어", code: "KNIT" },
  { group: "니트", name: "카디건", code: "CARD" },
  { group: "팬츠", name: "팬츠", code: "PNTS" },
  { group: "팬츠", name: "데님", code: "DNIM" },
  { group: "팬츠", name: "카고 팬츠", code: "CARG" },
  { group: "팬츠", name: "유틸리티 팬츠", code: "WPNT" },
  { group: "쇼츠", name: "쇼츠", code: "SHOR" },
  { group: "쇼츠", name: "스윔 쇼츠", code: "SWIM" },
  { group: "헤드웨어", name: "캡", code: "CAPS" },
  { group: "헤드웨어", name: "비니", code: "BEAN" },
  { group: "헤드웨어", name: "버킷햇", code: "HATS" },
  { group: "가방", name: "토트백", code: "TOTE" },
  { group: "가방", name: "백팩", code: "BKPK" },
  { group: "가방", name: "크로스백", code: "CSBD" },
  { group: "가방", name: "더플백", code: "DUFF" },
  { group: "가방", name: "툴백", code: "TBAG" },
  { group: "가방", name: "파우치", code: "PCH" },
  { group: "액세서리", name: "양말", code: "SOCK" },
  { group: "액세서리", name: "벨트", code: "BELT" },
  { group: "액세서리", name: "선글라스", code: "SUNG" },
  { group: "액세서리", name: "장갑", code: "GLVS" },
  { group: "액세서리", name: "스카프", code: "SCAR" },
  { group: "액세서리", name: "키링", code: "KYRG" },
  { group: "신발", name: "신발", code: "SHOE" },
  { group: "신발", name: "스니커즈", code: "SNKR" },
  { group: "라이프스타일", name: "스케이트 데크", code: "SKAT" },
  { group: "라이프스타일", name: "오브제", code: "OBJT" },
  { group: "기타", name: "미분류", code: "GENR" }
];

export function ProductDetailPage({ data }: ProductDetailPageProps) {
  const { productKey = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const decodedProductKey = decodeURIComponent(productKey);
  const product = useMemo(
    () => productRows(data).find((row) => row.productKey === decodedProductKey),
    [data, decodedProductKey]
  );
  const [form, setForm] = useState<ProductForm>(() => product ? productForm(product) : emptyProductForm());
  const initialForm = useMemo(() => product ? productForm(product) : emptyProductForm(), [product]);
  const [reason, setReason] = useState("");
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const channelProducts = useMemo(() => Array.isArray(data.inventory?.channelProducts) ? data.inventory.channelProducts as AnyRecord[] : [], [data]);
  const channels = useMemo(() => Array.isArray(data.inventory?.channels) ? data.inventory.channels as AnyRecord[] : [], [data]);
  const channelOptions = useMemo(() => channelProductOptions(channelProducts, channels), [channelProducts, channels]);
  const [selectedChannelCode, setSelectedChannelCode] = useState("");
  const matchedProductIds = useMemo(() => {
    if (!product) return new Set<string>();
    return new Set(product.rows.flatMap((row) => channelListings(row).map((listing) => String(listing.externalProductId || ""))).filter(Boolean));
  }, [product]);
  const matchedChannelProducts = useMemo(() => {
    if (!product) return [];
    return channelProducts.filter((item) => {
      if (String(item.matchedProductCode || "") === product.productKey) return true;
      return matchedProductIds.has(String(item.externalProductId || ""));
    });
  }, [channelProducts, matchedProductIds, product]);
  const candidateChannelProducts = useMemo(() => {
    const activeProducts = channelProducts.filter((item) => item.active !== false);
    const channelFiltered = selectedChannelCode
      ? activeProducts.filter((item) => channelProductCode(item) === selectedChannelCode)
      : activeProducts;
    return sortChannelProductCandidates(channelFiltered, product?.productName || "");
  }, [channelProducts, product?.productName, selectedChannelCode]);
  const [selectedChannelProductId, setSelectedChannelProductId] = useState("");
  const [selectedColorwayCode, setSelectedColorwayCode] = useState("");
  const colorways = form.colorways.length ? form.colorways : colorwaysFor(product);
  const selectedColorway = colorways.find((item) => item.code === selectedColorwayCode);
  const selectedChannelProduct = candidateChannelProducts.find((item) => String(item.id || "") === selectedChannelProductId);
  const dirty = JSON.stringify(form) !== JSON.stringify(initialForm);
  const categoryOptions = productCategoryOptions(form.categoryCode4);
  const selectedCategoryPreset = productCategoryPresetForCode(form.categoryCode4);
  const reviewItems = product ? productReviewItems(product) : [];

  useEffect(() => {
    setForm(initialForm);
  }, [initialForm]);

  useEffect(() => {
    if (!selectedChannelCode && channelOptions[0]) setSelectedChannelCode(channelOptions[0].code);
    if (selectedChannelCode && channelOptions.length && !channelOptions.some((item) => item.code === selectedChannelCode)) {
      setSelectedChannelCode(channelOptions[0].code);
    }
  }, [channelOptions, selectedChannelCode]);

  useEffect(() => {
    if (!selectedChannelProductId && candidateChannelProducts[0]) {
      setSelectedChannelProductId(String(candidateChannelProducts[0].id || ""));
    }
    if (selectedChannelProductId && candidateChannelProducts.length && !candidateChannelProducts.some((item) => String(item.id || "") === selectedChannelProductId)) {
      setSelectedChannelProductId(String(candidateChannelProducts[0].id || ""));
    }
    if (selectedChannelProductId && !candidateChannelProducts.length) setSelectedChannelProductId("");
  }, [candidateChannelProducts, selectedChannelProductId]);

  const selectedChannelName = channelOptions.find((item) => item.code === selectedChannelCode)?.name || "온라인몰";

  useEffect(() => {
    if (colorways.length && !colorways.some((item) => item.code === selectedColorwayCode)) {
      setSelectedColorwayCode(colorways[0].code);
    }
    if (!colorways.length && selectedColorwayCode) setSelectedColorwayCode("");
  }, [colorways, selectedColorwayCode]);

  const saveProduct = useMutation({
    mutationFn: () => {
      if (!product) throw new Error("상품을 찾지 못했습니다.");
      return api.updateProduct(product.productKey, {
        productName: form.productName,
        categoryCode: form.categoryCode,
        categoryCode4: form.categoryCode4,
        price: form.price,
        summaryDescription: form.summaryDescription,
        description: form.description,
        sizeGuide: form.sizeGuide,
        materialCare: form.materialCare,
        colorways: form.colorways
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    }
  });

  const syncProducts = useMutation({
    mutationFn: api.syncProducts,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["bootstrap"] })
  });

  const registerProduct = useMutation({
    mutationFn: () => {
      if (!product) throw new Error("상품을 찾지 못했습니다.");
      return api.registerProductOnChannel(product.productKey, { channelCode: "CAFE24_KR", reason });
    },
    onSuccess: async () => {
      setPendingAction(null);
      setReason("");
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    }
  });

  const matchProduct = useMutation({
    mutationFn: () => {
      if (!product) throw new Error("상품을 찾지 못했습니다.");
      if (!selectedChannelProduct) throw new Error("Cafe24 상품을 선택해 주세요.");
      if (!selectedColorway) throw new Error("컬러웨이를 선택해 주세요.");
      return api.matchChannelProduct(product.productKey, {
        channelProductId: Number(selectedChannelProduct.id),
        colorwayCode: selectedColorway.code,
        reason
      });
    },
    onSuccess: async () => {
      setPendingAction(null);
      setReason("");
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    }
  });

  const deleteProduct = useMutation({
    mutationFn: () => {
      if (!product) throw new Error("상품을 찾지 못했습니다.");
      return api.deleteProduct(product.productKey, { reason });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
      navigate("/products");
    }
  });

  if (!product) {
    return (
      <section className="panel">
        <EmptyState title="상품을 찾지 못했습니다." body="목록에서 다시 상품을 선택해 주세요." />
        <div className="panel-actions">
          <Link className="text-link" to="/products">Products로 돌아가기</Link>
        </div>
      </section>
    );
  }

  const error = saveProduct.error || syncProducts.error || registerProduct.error || matchProduct.error || deleteProduct.error;

  return (
    <div className="product-detail-page">
      <div className="record-toolbar">
        <Link className="back-link" to="/products">
          <ArrowLeft size={15} />
          Products
        </Link>
        <div className="record-title">
          <span>{product.productKey}</span>
          <h2>{product.productName}</h2>
          <p>{product.seasonCode} · {product.categoryName} · {formatNumber(product.skuCount)} SKU</p>
        </div>
        <div className="record-actions">
          <button type="button" onClick={() => setForm(initialForm)} disabled={!dirty || saveProduct.isPending}>되돌리기</button>
          <button type="button" className="primary" onClick={() => saveProduct.mutate()} disabled={!dirty || saveProduct.isPending}>
            {saveProduct.isPending ? "저장 중" : "상품 정보 저장"}
          </button>
        </div>
      </div>

      {error ? <p className="form-error">{(error as Error).message}</p> : null}

      <div className="product-detail-layout">
        <main className="product-detail-main">
          {reviewItems.length ? <ProductReviewPanel items={reviewItems} /> : null}

          <section className="section-panel">
            <div className="section-head">
              <div>
                <h3>기본 정보</h3>
                <p>상품명, 시즌, 카테고리를 수정합니다.</p>
              </div>
            </div>
            <div className="form-grid">
              <label>
                상품명
                <input value={form.productName} onChange={(event) => setForm((prev) => ({ ...prev, productName: event.target.value }))} />
              </label>
              <label>
                시즌
                <input value={product.seasonCode} disabled />
              </label>
              <label>
                카테고리
                <select
                  value={form.categoryCode4}
                  onChange={(event) => setForm((prev) => ({ ...prev, categoryCode: event.target.value, categoryCode4: event.target.value }))}
                >
                  {categoryOptions.map((category) => (
                    <option key={category.code} value={category.code}>
                      {categoryOptionLabel(category)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                카테고리 코드
                <input value={form.categoryCode4 || "-"} readOnly />
              </label>
            </div>
            <p className="field-hint">
              {selectedCategoryPreset
                ? `${selectedCategoryPreset.group} / ${selectedCategoryPreset.name} 선택 시 ${selectedCategoryPreset.code} 코드가 SKU alias와 온라인몰 매핑 기준에 같이 반영됩니다.`
                : "기존 상품에 등록되지 않은 카테고리 코드가 있습니다. 저장 전 등록된 카테고리 중 하나로 선택해 주세요."}
            </p>
          </section>

          <section className="section-panel">
            <div className="section-head">
              <div>
                <h3>컬러웨이</h3>
                <p>컬러 이름과 컬러웨이 코드를 관리합니다. 색상 원은 이름 기반 미리보기입니다.</p>
              </div>
              <button
                type="button"
                onClick={() => setForm((prev) => ({
                  ...prev,
                  colorways: [...prev.colorways, { code: nextColorwayCode(prev.colorways), name: "" }]
                }))}
              >
                <Plus size={15} />
                컬러 추가
              </button>
            </div>
            <div className="colorway-edit-list">
              {form.colorways.map((colorway, index) => {
                const swatch = colorSwatchForName(colorway.name);
                const canRemove = form.colorways.length > 1;
                return (
                  <div className="colorway-edit-row" key={colorway.code}>
                    <span
                      className="colorway-swatch"
                      style={{ background: swatch.background, borderColor: swatch.borderColor }}
                      aria-label={`${colorway.name || colorway.code} 컬러 미리보기`}
                      title={swatch.inferred ? "컬러명 기반 미리보기" : "정확한 색상값 없음"}
                    />
                    <label>
                      컬러웨이 코드
                      <input aria-label={`컬러웨이 코드 ${colorway.code}`} value={colorway.code} readOnly />
                    </label>
                    <label>
                      컬러 이름
                      <input
                        aria-label={`컬러 이름 ${colorway.code}`}
                        value={colorway.name}
                        placeholder="예: Faded Black"
                        onChange={(event) => setForm((prev) => ({
                          ...prev,
                          colorways: prev.colorways.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item)
                        }))}
                      />
                    </label>
                    <button
                      type="button"
                      aria-label={`컬러웨이 ${colorway.code} 삭제`}
                      disabled={!canRemove}
                      onClick={() => setForm((prev) => ({
                        ...prev,
                        colorways: prev.colorways.filter((item) => item.code !== colorway.code)
                      }))}
                    >
                      삭제
                    </button>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="section-panel">
            <div className="section-head">
              <div>
                <h3>판매 정보</h3>
                <p>상품의 기준 판매가를 저장합니다. 채널별 가격 적용은 온라인몰 작업에서 별도로 확인합니다.</p>
              </div>
            </div>
            <div className="form-grid">
              <label>
                기준 판매가
                <input type="number" min={0} value={form.price} onChange={(event) => setForm((prev) => ({ ...prev, price: Math.max(0, Number(event.target.value || 0)) }))} />
              </label>
              <label>
                통화
                <input value="KRW" readOnly />
              </label>
            </div>
          </section>

          <section className="section-panel">
            <div className="section-head">
              <div>
                <h3>콘텐츠</h3>
                <p>온라인몰 상품 설명에 쓰는 문구를 관리합니다. 이미지는 별도 이미지 작업에서 처리합니다.</p>
              </div>
            </div>
            <div className="form-grid">
              <label className="span-2">
                요약 설명
                <input value={form.summaryDescription} onChange={(event) => setForm((prev) => ({ ...prev, summaryDescription: event.target.value }))} placeholder="온라인몰 목록이나 상품 상단에 쓰는 짧은 설명" />
              </label>
              <label className="span-2">
                상품 설명
                <textarea value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} />
              </label>
              <label>
                사이즈 가이드
                <textarea value={form.sizeGuide} onChange={(event) => setForm((prev) => ({ ...prev, sizeGuide: event.target.value }))} />
              </label>
              <label>
                소재/관리
                <textarea value={form.materialCare} onChange={(event) => setForm((prev) => ({ ...prev, materialCare: event.target.value }))} />
              </label>
            </div>
          </section>

          <section className="section-panel">
            <div className="section-head">
              <div>
                <h3>SKU 매트릭스</h3>
                <p>SKU, 사람이 읽는 코드, 바코드, 재고, 온라인 할당을 확인합니다.</p>
              </div>
            </div>
            <div className="table-frame">
              <table className="data-table product-sku-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Readable alias</th>
                    <th>바코드</th>
                    <th>컬러</th>
                    <th>사이즈</th>
                    <th>실재고</th>
                    <th>온라인 할당</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSkuRows(product.rows).map((row) => (
                    <tr key={String(row.sku || row.readableAlias)}>
                      <td><strong>{row.sku}</strong></td>
                      <td>{row.readableAlias || "-"}</td>
                      <td>
                        {barcodeValue(row) ? (
                          <Link
                            className="barcode-chip"
                            aria-label={`${row.sku || row.readableAlias || "SKU"} 바코드 라벨`}
                            to={`/products/barcodes?sku=${encodeURIComponent(String(row.sku || ""))}`}
                          >
                            {barcodeValue(row)}
                          </Link>
                        ) : <span className="barcode-chip barcode-chip--empty">미발급</span>}
                      </td>
                      <td>{row.colorwayName || row.colorwayCode || "-"}</td>
                      <td>{formatSizeLabel(row.sizeName || row.sizeCode)}</td>
                      <td className="num">{formatNumber(row.onHandTotal || 0)}</td>
                      <td className="num">{formatNumber(row.allocatedTotal || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="section-panel">
            <div className="section-head">
              <div>
                <h3>온라인몰 매칭</h3>
                <p>온라인몰에 등록된 상품과 백오피스 컬러웨이를 연결합니다. 상품 후보는 온라인몰에서 읽은 정보만 기준으로 보여줍니다.</p>
              </div>
              <button type="button" onClick={() => syncProducts.mutate()} disabled={syncProducts.isPending}>
                <RefreshCcw size={15} />
                {syncProducts.isPending ? "읽는 중" : `${selectedChannelName} 상품 읽기`}
              </button>
            </div>
            <div className="channel-summary-grid">
              <Metric label="Cafe24 매핑 SKU" value={`${channelListingsForProduct(product).length}/${product.skuCount}`} />
              <Metric label="Cafe24 현재고" value={formatNumber(channelListingsForProduct(product).reduce((sum, listing) => sum + Number(listing.remoteQuantity || 0), 0))} />
              <Metric label="연결 상품" value={`${matchedChannelProducts.length}개`} />
            </div>
            <div className="table-frame">
              <table className="data-table product-sku-table">
                <thead>
                  <tr>
                    <th>내부 SKU</th>
                    <th>컬러/사이즈</th>
                    <th>Cafe24 상품번호</th>
                    <th>Cafe24 상품코드</th>
                    <th>Cafe24 품목코드</th>
                    <th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSkuRows(product.rows).map((row) => {
                    const listing = primaryListing(row);
                    const cafe24ProductCode = channelListingProductCode(listing, channelProducts);
                    return (
                      <tr key={`mall-${String(row.sku || row.readableAlias)}`}>
                        <td><strong>{row.sku}</strong></td>
                        <td>{row.colorwayName || row.colorwayCode} / {formatSizeLabel(row.sizeName || row.sizeCode)}</td>
                        <td>{compactLabel(listing?.externalProductId)}</td>
                        <td>{compactLabel(cafe24ProductCode)}</td>
                        <td>{compactLabel(listing?.externalVariantId || row.variantCode)}</td>
                        <td>{listing ? <StatusBadge tone="success">매칭됨</StatusBadge> : <StatusBadge tone="warning">미매칭</StatusBadge>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="match-workbar">
              <label>
                온라인몰
                <select value={selectedChannelCode} onChange={(event) => setSelectedChannelCode(event.target.value)}>
                  {channelOptions.length ? channelOptions.map((channel) => (
                    <option key={channel.code} value={channel.code}>{channel.name}</option>
                  )) : <option value="">연결된 온라인몰 없음</option>}
                </select>
              </label>
              <label>
                등록 상품
                <select value={selectedChannelProductId} onChange={(event) => setSelectedChannelProductId(event.target.value)}>
                  {candidateChannelProducts.length ? candidateChannelProducts.map((item) => (
                    <option key={String(item.id)} value={String(item.id)}>
                      {channelProductSelectLabel(item)}
                    </option>
                  )) : <option value="">읽어온 상품 없음</option>}
                </select>
              </label>
              <label>
                연결할 백오피스 컬러웨이
                <select value={selectedColorwayCode} onChange={(event) => setSelectedColorwayCode(event.target.value)}>
                  {colorways.map((colorway) => <option key={colorway.code} value={colorway.code}>{colorway.name} · {colorway.code}</option>)}
                </select>
              </label>
              <button type="button" onClick={() => setPendingAction("match")} disabled={!selectedChannelProduct || !selectedColorway}>
                선택 상품 매칭
              </button>
              <button type="button" className="primary" onClick={() => setPendingAction("register")}>
                {selectedChannelName}에 상품 등록/매핑
              </button>
            </div>
            {matchedChannelProducts.length ? (
              <div className="matched-products">
                {matchedChannelProducts.map((item) => {
                  const descriptor = channelProductDescriptor(item, product, colorways);
                  const source = channelProductSource(item);
                  const swatch = colorSwatchForName(source.colorName);
                  return (
                    <div className="matched-product-row" key={String(item.id || item.externalProductId)}>
                      <div className="matched-product-main">
                        <span
                          className="colorway-swatch"
                          style={{ background: swatch.background, borderColor: swatch.borderColor }}
                          aria-label={`${source.colorName} 온라인몰 컬러 미리보기`}
                          title={swatch.inferred ? "컬러명 기반 미리보기" : "정확한 색상값 없음"}
                        />
                        <span>
                          <strong>{channelProductTitle(item)}</strong>
                          <small>백오피스 연결: {descriptor.colorLabel}</small>
                        </span>
                      </div>
                      <span>{channelProductMeta(item)}</span>
                      <StatusBadge tone="success">연결됨</StatusBadge>
                    </div>
                  );
                })}
              </div>
            ) : <p className="muted">아직 연결된 온라인몰 상품 레코드가 없습니다. 이미 온라인몰에 상품이 있다면 위에서 상품을 읽은 뒤 컬러웨이와 매칭하세요.</p>}
          </section>

          <section className="section-panel danger-section">
            <div className="section-head">
              <div>
                <h3>위험 작업</h3>
                <p>삭제는 SKU와 재고 원장에 영향을 주므로 사유와 영향 확인 후 실행합니다.</p>
              </div>
              <button type="button" className="danger" onClick={() => setPendingAction("delete")}>
                <Trash2 size={15} />
                상품 삭제
              </button>
            </div>
          </section>
        </main>

        <aside className="product-context-panel">
          <h3>운영 상태</h3>
          <KeyValueLite label="내부 상품코드" value={product.productKey} />
          <KeyValueLite label="판매가" value={formatMoney(form.price)} />
          <KeyValueLite label="실재고" value={`${formatNumber(product.onHandTotal)}개`} />
          <KeyValueLite label="온라인 할당" value={`${formatNumber(product.allocatedTotal)}개`} />
          <KeyValueLite label="Cafe24 매칭" value={product.channelStatus} />
          <KeyValueLite label="최근 수정" value={formatDateTime(product.source.updatedAt)} />
          <div className="context-note">
            <strong>작업 원칙</strong>
            <p>상품 정보 저장은 백오피스에 먼저 반영됩니다. Cafe24 등록/매칭은 별도 확인을 거쳐 외부 영향과 감사 로그를 남깁니다.</p>
          </div>
        </aside>
      </div>

      <ConfirmImpactDialog
        open={Boolean(pendingAction)}
        title={confirmTitle(pendingAction)}
        currentValue={confirmCurrentValue(pendingAction, product, selectedChannelProduct, selectedColorway)}
        nextValue={confirmNextValue(pendingAction, product, selectedChannelProduct, selectedColorway)}
        externalImpact={confirmExternalImpact(pendingAction)}
        auditTrail={confirmAuditTrail(pendingAction, product)}
        reason={reason}
        confirmLabel={confirmLabel(pendingAction)}
        tone={pendingAction === "delete" ? "danger" : "default"}
        busy={registerProduct.isPending || matchProduct.isPending || deleteProduct.isPending}
        onReasonChange={setReason}
        onCancel={() => {
          setPendingAction(null);
          setReason("");
        }}
        onConfirm={() => {
          if (pendingAction === "register") registerProduct.mutate();
          if (pendingAction === "match") matchProduct.mutate();
          if (pendingAction === "delete") deleteProduct.mutate();
        }}
      />
    </div>
  );
}

function emptyProductForm(): ProductForm {
  return {
    productName: "",
    categoryCode: "",
    categoryCode4: "",
    price: 0,
    summaryDescription: "",
    description: "",
    sizeGuide: "",
    materialCare: "",
    colorways: []
  };
}

function productForm(product: ProductRow): ProductForm {
  return {
    productName: product.productName,
    categoryCode: productCategoryCode(product.source.categoryCode || product.categoryName),
    categoryCode4: productCategoryCode(product.source.categoryCode4 || product.categoryName),
    price: Number(product.source.price || product.rows.find((row) => Number(row.price || 0) > 0)?.price || 0),
    summaryDescription: String(product.source.summaryDescription || product.rows[0]?.productSummaryDescription || ""),
    description: String(product.source.description || product.rows[0]?.productDescription || ""),
    sizeGuide: String(product.source.sizeGuide || product.rows[0]?.sizeGuide || ""),
    materialCare: String(product.source.materialCare || product.rows[0]?.materialCare || ""),
    colorways: colorwaysFor(product)
  };
}

function colorwaysFor(product?: ProductRow): ColorwayEdit[] {
  if (!product) return [];
  const options = Array.isArray(product.source.options) ? product.source.options as AnyRecord[] : [];
  const colorOptions = options.filter((option) => option.optionType === "color");
  if (colorOptions.length) {
    return colorOptions.map((option) => ({
      code: String(option.optionCode || ""),
      name: String(option.optionName || option.optionCode || "")
    })).filter((item) => item.code);
  }
  const map = new Map<string, string>();
  for (const row of product.rows) {
    const code = String(row.colorwayCode || "");
    if (code && !map.has(code)) map.set(code, String(row.colorwayName || code));
  }
  return [...map.entries()].map(([code, name]) => ({ code, name }));
}

function productReviewItems(product: ProductRow): ProductReviewItem[] {
  const items: ProductReviewItem[] = [];
  if (product.channelStatus === "매핑 필요") {
    items.push({
      key: "channel",
      title: "온라인몰 매핑 없음",
      body: "온라인몰 상품 또는 품목과 내부 SKU 연결을 확인해야 합니다.",
      status: "매핑 필요",
      tone: "warning"
    });
  }
  return items;
}

function ProductReviewPanel({ items }: { items: ProductReviewItem[] }) {
  return (
    <section className="section-panel product-review-panel" aria-labelledby="product-review-heading">
      <div className="section-head">
        <div>
          <h3 id="product-review-heading">확인 필요</h3>
          <p>이 상품이 확인 필요 보기에 포함된 이유입니다.</p>
        </div>
      </div>
      <div className="product-review-list">
        {items.map((item) => (
          <div className="product-review-row" key={item.key}>
            <div>
              <strong>{item.title}</strong>
              <span>{item.body}</span>
            </div>
            <StatusBadge tone={item.tone}>{item.status}</StatusBadge>
          </div>
        ))}
      </div>
    </section>
  );
}

function productCategoryCode(value: unknown): string {
  return String(value || "").trim().toUpperCase();
}

function productCategoryPresetForCode(code: string): ProductCategoryPreset | undefined {
  const normalizedCode = productCategoryCode(code);
  return PRODUCT_CATEGORY_PRESETS.find((category) => category.code === normalizedCode);
}

function productCategoryOptions(currentCode: string): ProductCategoryPreset[] {
  const normalizedCode = productCategoryCode(currentCode);
  if (!normalizedCode || productCategoryPresetForCode(normalizedCode)) return PRODUCT_CATEGORY_PRESETS;
  return [{ group: "기존 값", name: "등록되지 않은 카테고리", code: normalizedCode }, ...PRODUCT_CATEGORY_PRESETS];
}

function categoryOptionLabel(category: ProductCategoryPreset): string {
  return `${category.group} / ${category.name} (${category.code})`;
}

function nextColorwayCode(colorways: ColorwayEdit[]): string {
  const max = colorways.reduce((currentMax, colorway) => {
    const numericCode = Number(String(colorway.code || "").replace(/\D/g, ""));
    return Number.isFinite(numericCode) ? Math.max(currentMax, numericCode) : currentMax;
  }, 0);
  return String(max + 1).padStart(3, "0").slice(-3);
}

function colorSwatchForName(name: string): { background: string; borderColor: string; inferred: boolean } {
  const text = name.trim().toLowerCase();
  const matches = [
    { keys: ["faded black", "washed black", "black", "charcoal", "검정", "블랙"], color: "#2f302f", border: "#202020" },
    { keys: ["dust blue", "blue", "navy", "파랑", "블루", "네이비"], color: "#7f96a5", border: "#6f8798" },
    { keys: ["off white", "ivory", "cream", "white", "아이보리", "화이트", "흰"], color: "#f3efe4", border: "#d8d3c5" },
    { keys: ["grey", "gray", "silver", "회색", "그레이"], color: "#a8aaa7", border: "#8e908d" },
    { keys: ["green", "olive", "khaki", "초록", "카키"], color: "#75846b", border: "#65745b" },
    { keys: ["brown", "tan", "beige", "브라운", "베이지"], color: "#a58c70", border: "#8f775e" },
    { keys: ["red", "burgundy", "빨강", "레드"], color: "#a9433d", border: "#91352f" }
  ];
  const match = matches.find((item) => item.keys.some((key) => text.includes(key)));
  if (match) return { background: match.color, borderColor: match.border, inferred: true };
  return { background: "#f5f5f5", borderColor: "#d4d4d4", inferred: false };
}

function barcodeValue(row: SkuRow): string {
  return String(row.barcode || row.gtin || row.ean13 || row.ean || "").trim();
}

function sortedSkuRows(rows: SkuRow[]): SkuRow[] {
  return [...rows].sort((a, b) =>
    String(a.colorwayName || a.colorwayCode || "").localeCompare(String(b.colorwayName || b.colorwayCode || ""), "ko") ||
    String(a.sizeName || a.sizeCode || "").localeCompare(String(b.sizeName || b.sizeCode || ""), "ko")
  );
}

function channelListings(row: SkuRow): AnyRecord[] {
  return Array.isArray(row.channelListings) ? row.channelListings as AnyRecord[] : [];
}

function primaryListing(row: SkuRow): AnyRecord | undefined {
  return channelListings(row).find((listing) => listing.active !== false) || channelListings(row)[0];
}

function channelListingProductCode(listing: AnyRecord | undefined, channelProducts: AnyRecord[]): string {
  if (!listing) return "";
  const externalProductId = String(listing.externalProductId || "");
  const channelCode = String(listing.channelCode || "");
  const product = channelProducts.find((item) =>
    String(item.externalProductId || "") === externalProductId
      && (!channelCode || channelProductCode(item) === channelCode)
  );
  return externalProductCode(product || {}) || String(listing.externalProductCode || "").trim();
}

function channelListingsForProduct(product: ProductRow): AnyRecord[] {
  return product.rows.flatMap(channelListings).filter((listing) => listing.active !== false);
}

type ChannelProductDescriptor = {
  colorLabel: string;
  colorName: string;
};

type ChannelProductOption = {
  code: string;
  name: string;
};

type ChannelProductSource = {
  productName: string;
  colorName: string;
  productCode: string;
};

function channelProductOptions(channelProducts: AnyRecord[], channels: AnyRecord[]): ChannelProductOption[] {
  const options = new Map<string, string>();
  for (const channel of channels) {
    if (!isOnlineMallChannel(channel)) continue;
    const code = String(channel.code || channel.channelCode || "").trim();
    if (!code || options.has(code)) continue;
    options.set(code, String(channel.name || channel.channelName || code).trim());
  }
  for (const item of channelProducts) {
    if (item.active === false) continue;
    if (!isOnlineMallChannel(item)) continue;
    const code = channelProductCode(item);
    if (!code || options.has(code)) continue;
    options.set(code, channelProductName(item));
  }
  return [...options.entries()].map(([code, name]) => ({ code, name }));
}

function isOnlineMallChannel(item: AnyRecord): boolean {
  const kind = String(item.kind || item.channelKind || "").toLowerCase();
  const provider = String(item.provider || "").toLowerCase();
  const code = String(item.code || item.channelCode || "").toUpperCase();
  return kind !== "offline_pos" && provider !== "pos" && !code.includes("POS");
}

function channelProductCode(item: AnyRecord): string {
  return String(item.channelCode || item.channel_id || item.channelId || "ONLINE").trim();
}

function channelProductName(item: AnyRecord): string {
  return String(item.channelName || item.channelDisplayName || item.provider || item.channelCode || "온라인몰").trim();
}

function channelProductSelectLabel(item: AnyRecord): string {
  const source = channelProductSource(item);
  return [source.productName, source.colorName, source.productCode].join(" · ");
}

function channelProductTitle(item: AnyRecord): string {
  const source = channelProductSource(item);
  return [channelProductName(item), source.productName, source.colorName, source.productCode].filter(Boolean).join(" · ");
}

function channelProductMeta(item: AnyRecord): string {
  const externalProductId = compactLabel(item.externalProductId);
  const externalProductCode = channelProductSource(item).productCode;
  const variantCount = Array.isArray(item.variants)
    ? item.variants.filter((variant) => !isSyntheticChannelProductVariant(variant as AnyRecord)).length
    : 0;
  return [`상품번호 ${externalProductId}`, externalProductCode, `${formatNumber(variantCount)} 품목`].filter(Boolean).join(" · ");
}

function channelProductDescriptor(item: AnyRecord, product: ProductRow, colorways: ColorwayEdit[]): ChannelProductDescriptor {
  const colorway = channelProductColorway(item, product, colorways);
  const colorName = colorway?.name || "컬러 미확인";
  const colorLabel = colorway?.code ? `${colorName} · ${colorway.code}` : colorName;
  return {
    colorLabel,
    colorName
  };
}

function channelProductSource(item: AnyRecord): ChannelProductSource {
  return {
    productName: externalProductName(item),
    colorName: externalProductColorLabel(item) || "컬러명 없음",
    productCode: externalProductCode(item) || "상품코드 없음"
  };
}

function externalProductName(item: AnyRecord): string {
  return String(item.externalName || item.externalDisplay || item.raw?.product_name || item.raw?.productName || item.externalProductId || "온라인몰 상품").trim();
}

function externalProductColorLabel(item: AnyRecord): string {
  const raw = item.raw && typeof item.raw === "object" ? item.raw as AnyRecord : {};
  const directColor = [
    item.externalColorName,
    item.colorName,
    item.color,
    raw.color,
    raw.color_name,
    raw.colorName,
    raw.colour,
    raw.colour_name,
    raw.product_color,
    raw.productColor,
    raw.option_color,
    raw.optionColor
  ].map((value) => String(value || "").trim()).find(Boolean);
  if (directColor) return directColor;
  const productOptionColor = uniqueValues([
    ...optionValuesByName(raw.options, COLOR_OPTION_NAMES),
    ...optionValuesByName(raw.product_options, COLOR_OPTION_NAMES),
    ...optionValuesByName(raw.productOptions, COLOR_OPTION_NAMES),
    ...colorValuesFromOptionText([
      raw.optionText,
      raw.option_text,
      raw.option_value,
      raw.option_name,
      raw.options_text
    ].filter(Boolean).join(" "))
  ].map((value) => String(value || "").trim()).filter((value) => value && !isLikelySizeValue(value)));
  if (productOptionColor.length === 1) return productOptionColor[0];
  const variantColor = externalProductColorFromVariants(item.variants);
  if (variantColor) return variantColor;
  return "";
}

function externalProductCode(item: AnyRecord): string {
  const raw = item.raw && typeof item.raw === "object" ? item.raw as AnyRecord : {};
  return [
    raw.product_code,
    raw.productCode,
    raw.code,
    item.productCode,
    item.product_code,
    item.externalProductCode,
    raw.external_product_code,
    raw.externalProductCode,
    raw.custom_product_code,
    raw.customProductCode
  ].map((value) => String(value || "").trim()).find(Boolean) || "";
}

function externalProductColorFromVariants(variants: unknown): string {
  if (!Array.isArray(variants)) return "";
  const candidates = new Set<string>();
  for (const variant of variants) {
    const record = variant as AnyRecord;
    if (isSyntheticChannelProductVariant(record)) continue;
    for (const value of externalVariantColorValues(record)) candidates.add(value);
  }
  return candidates.size === 1 ? [...candidates][0] : "";
}

function isSyntheticChannelProductVariant(record: AnyRecord): boolean {
  const raw = record.raw && typeof record.raw === "object" ? record.raw as AnyRecord : {};
  const variantId = normalizeOptionLabel([
    record.externalVariantId,
    record.variantCode,
    raw.variant_code,
    raw.variantCode,
    raw.item_code,
    raw.itemCode,
    raw.id
  ].map((value) => String(value || "").trim()).find(Boolean) || "");
  const optionName = normalizeOptionLabel([
    record.externalOptionName,
    raw.optionText,
    raw.option_text,
    raw.option_value,
    raw.option_name
  ].map((value) => String(value || "").trim()).find(Boolean) || "");
  const source = normalizeOptionLabel(raw.source || "");
  return variantId === "product" || optionName === "cafe24product" || source === "legacycafe24import";
}

function externalVariantColorValues(record: AnyRecord): string[] {
  const raw = record.raw && typeof record.raw === "object" ? record.raw as AnyRecord : {};
  const directValues = [
    record.externalColorName,
    record.colorName,
    record.color,
    raw.color,
    raw.color_name,
    raw.colorName,
    raw.colour,
    raw.colour_name,
    raw.product_color,
    raw.productColor,
    raw.option_color,
    raw.optionColor
  ];
  const optionValues = optionValuesByName(raw.options, COLOR_OPTION_NAMES);
  const structuredValues = [...directValues, ...optionValues]
    .map((value) => String(value || "").trim())
    .filter((value) => value && !isLikelySizeValue(value));
  if (structuredValues.length) return uniqueValues(structuredValues);
  const optionTextValues = colorValuesFromOptionText([
    record.externalOptionName,
    raw.optionText,
    raw.option_text,
    raw.option_value,
    raw.option_name,
    raw.options_text
  ].filter(Boolean).join(" "));
  return [...directValues, ...optionValues, ...optionTextValues]
    .map((value) => String(value || "").trim())
    .filter((value) => value && !isLikelySizeValue(value));
}

function optionValuesByName(options: unknown, optionNames: string[]): string[] {
  const parsedOptions = parseMaybeJson(options);
  if (parsedOptions) return optionValuesByName(parsedOptions, optionNames);
  const wanted = new Set(optionNames.map(normalizeOptionLabel));
  if (Array.isArray(options)) {
    return options
      .filter((option) => {
        const record = option as AnyRecord;
        const optionName = normalizeOptionLabel(record.name || record.option_name || record.optionName || record.key || record.label || "");
        return wanted.has(optionName);
      })
      .map((option) => {
        const record = option as AnyRecord;
        return String(record.value || record.option_value || record.optionValue || record.text || record.name_value || record.nameValue || "").trim();
      })
      .filter(Boolean);
  }
  if (options && typeof options === "object") {
    return Object.entries(options as Record<string, unknown>)
      .filter(([key]) => wanted.has(normalizeOptionLabel(key)))
      .map(([, value]) => String(value || "").trim())
      .filter(Boolean);
  }
  return [];
}

function parseMaybeJson(value: unknown): unknown {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || !/^[\[{]/.test(trimmed)) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

function uniqueValues(values: string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

function colorValuesFromOptionText(value: string): string[] {
  const text = value.trim();
  if (!text) return [];
  const parts = text.split(/\s*(?:\/|,|;|\||>|·)\s*/).map((item) => item.trim()).filter(Boolean);
  if (parts.length >= 2 && !isLikelySizeValue(parts[0])) return [parts[0]];
  if (parts.length === 1 && !isLikelySizeValue(parts[0])) return [parts[0]];
  return [];
}

function normalizeOptionLabel(value: unknown): string {
  return String(value || "")
    .normalize("NFKC")
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "");
}

function isLikelySizeValue(value: unknown): boolean {
  const normalized = normalizeOptionLabel(value);
  return /^(xxs|xs|s|m|l|xl|xxl|xxxl|os|free|one|onesize|one사이즈|free사이즈|small|medium|large|xlarge|스몰|미디움|라지|프리)$/.test(normalized)
    || /^[0-9]{2,3}$/.test(normalized);
}

function sortChannelProductCandidates(items: AnyRecord[], currentProductName: string): AnyRecord[] {
  const currentName = normalizeProductNameForSort(currentProductName);
  return [...items].sort((a, b) => {
    const aName = normalizeProductNameForSort(externalProductName(a));
    const bName = normalizeProductNameForSort(externalProductName(b));
    const aExact = currentName && aName === currentName ? 0 : 1;
    const bExact = currentName && bName === currentName ? 0 : 1;
    if (aExact !== bExact) return aExact - bExact;
    const aContains = currentName && aName.includes(currentName) ? 0 : 1;
    const bContains = currentName && bName.includes(currentName) ? 0 : 1;
    if (aContains !== bContains) return aContains - bContains;
    return channelProductSelectLabel(a).localeCompare(channelProductSelectLabel(b), "ko-KR", { numeric: true });
  });
}

function normalizeProductNameForSort(value: unknown): string {
  return String(value || "")
    .normalize("NFKC")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function channelProductColorway(item: AnyRecord, product: ProductRow, colorways: ColorwayEdit[]): ColorwayEdit | undefined {
  return colorwayFromMappedSkuRows(item, product)
    || colorwayFromCode(item.matchedColorCode, colorways)
    || colorwayFromCode(cafe24ProductCodeColorwayCode(item.externalProductCode), colorways)
    || colorwayFromVariants(item.variants, colorways);
}

function colorwayFromMappedSkuRows(item: AnyRecord, product: ProductRow): ColorwayEdit | undefined {
  const externalProductId = String(item.externalProductId || "");
  if (!externalProductId) return undefined;
  const rows = product.rows.filter((row) =>
    channelListings(row).some((listing) => String(listing.externalProductId || "") === externalProductId)
  );
  const colorMap = new Map<string, string>();
  for (const row of rows) {
    const code = String(row.colorwayCode || "");
    if (code && !colorMap.has(code)) colorMap.set(code, String(row.colorwayName || code));
  }
  if (colorMap.size !== 1) return undefined;
  const [[code, name]] = [...colorMap.entries()];
  return { code, name };
}

function colorwayFromCode(value: unknown, colorways: ColorwayEdit[]): ColorwayEdit | undefined {
  const normalizedValue = normalizeColorwayCode(value);
  if (!normalizedValue) return undefined;
  return colorways.find((colorway) => normalizeColorwayCode(colorway.code) === normalizedValue);
}

function cafe24ProductCodeColorwayCode(value: unknown): string {
  const parts = String(value || "").trim().split(/[-_\s/]+/).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : "";
}

function colorwayFromVariants(variants: unknown, colorways: ColorwayEdit[]): ColorwayEdit | undefined {
  if (!Array.isArray(variants)) return undefined;
  const variantText = variants.map((variant) => {
    const record = variant as AnyRecord;
    const raw = record.raw && typeof record.raw === "object" ? record.raw as AnyRecord : {};
    return [
      record.externalOptionName,
      record.externalSku,
      raw.optionText,
      raw.option_text,
      raw.option_value,
      raw.custom_variant_code
    ].filter(Boolean).join(" ");
  }).join(" ");
  const normalizedVariantText = normalizeColorwayCode(variantText);
  const haystack = variantText.toLowerCase();
  return colorways.find((colorway) => {
    const name = String(colorway.name || "").trim().toLowerCase();
    const code = normalizeColorwayCode(colorway.code);
    return Boolean(name && haystack.includes(name)) || Boolean(code && normalizedVariantText.includes(code));
  });
}

function normalizeColorwayCode(value: unknown): string {
  return String(value || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric-cell">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function KeyValueLite({ label, value }: { label: string; value: string }) {
  return (
    <div className="context-kv">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function confirmTitle(action: PendingAction): string {
  if (action === "register") return "Cafe24 상품 등록/매핑 확인";
  if (action === "match") return "Cafe24 등록 상품 매칭 확인";
  if (action === "delete") return "상품 삭제 확인";
  return "작업 확인";
}

function confirmCurrentValue(action: PendingAction, product: ProductRow, channelProduct?: AnyRecord, colorway?: ColorwayEdit): string {
  if (action === "match") return `${product.channelStatus} · 선택 상품 ${channelProduct?.externalName || "-"}`;
  if (action === "delete") return `${product.productName} · ${formatNumber(product.skuCount)} SKU · 실재고 ${formatNumber(product.onHandTotal)}개`;
  return `${product.productName} · ${product.channelStatus}`;
}

function confirmNextValue(action: PendingAction, product: ProductRow, channelProduct?: AnyRecord, colorway?: ColorwayEdit): string {
  if (action === "match") return `${channelProduct?.externalName || "Cafe24 상품"} → ${product.productName} / ${colorway?.name || "컬러웨이"}`;
  if (action === "delete") return "상품, 옵션, SKU, 위치 재고를 삭제";
  return "Cafe24 컬러웨이 상품 생성 또는 기존 상품 조회 후 SKU 매핑 저장";
}

function confirmExternalImpact(action: PendingAction): string {
  if (action === "register") return "Cafe24 상품/품목 생성 또는 조회가 실행될 수 있습니다. 재고 수량 적용은 별도 화면에서 처리합니다.";
  if (action === "match") return "Cafe24에는 쓰지 않고 백오피스의 상품-SKU 매핑과 대조 기준만 바뀝니다.";
  if (action === "delete") return "Cafe24 상품 삭제는 실행하지 않습니다. 백오피스 상품/SKU/재고 데이터는 제거됩니다.";
  return "-";
}

function confirmAuditTrail(action: PendingAction, product: ProductRow): string {
  if (action === "delete") return `products.delete · ${product.productKey} · 사유와 영향 기록`;
  if (action === "match") return `products.channel_product_match · ${product.productKey} · 선택 상품/컬러웨이 기록`;
  return `products.channel_register · ${product.productKey} · 생성/매핑 결과 기록`;
}

function confirmLabel(action: PendingAction): string {
  if (action === "delete") return "상품 삭제";
  if (action === "match") return "매칭 저장";
  if (action === "register") return "Cafe24 등록/매핑";
  return "확인";
}
